/**
 * @file        src/pages/seo/SeoRedirects.jsx
 * @description SEO Management → Redirects: the old-URL map the storefront
 *              enforces before it renders a page. Paginated list with hits and
 *              last hit, add/edit modal, delete, and a CSV import.
 *
 *              LOOPS AND CHAINS ARE REFUSED BY THE SERVER, against the whole
 *              set — a save that would create one answers 409 with the rows
 *              involved, shown in the modal. The CSV is parsed here only to
 *              turn a file into rows; every rule (paths, duplicates, loops) is
 *              the server's, all or nothing, with the failing line numbers.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-09 Claude — Initial (SEO Management).
 */
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import Select from '../../components/Select';
import TableSkeleton from '../../components/TableSkeleton';
import {
  createRedirect, deleteRedirect, importRedirects, listRedirects, updateRedirect,
} from '../../api/seo';
import { qk } from '../../api/queryKeys';
import { formatDateTime } from '../../lib/format';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { BrandBar, PickBrand, errFields, errMessage, useSeoBrand } from './seoShared';

const PAGE_SIZE = 30;
const STATUS_OPTIONS = [
  { value: '301', label: '301 — moved permanently' },
  { value: '302', label: '302 — moved temporarily' },
  { value: '410', label: '410 — gone (no destination)' },
];
const ACTIVE_FILTER = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];
const PROBLEM_LABEL = { self: 'Points at itself', chain: 'Chain', loop: 'Loop' };

/** Minimal RFC-4180-ish CSV: quoted fields, doubled quotes, comma separated. */
function parseCsv(text) {
  const rows = [];
  for (const rawLine of String(text || '').split(/\r?\n/)) {
    if (!rawLine.trim()) continue;
    const cells = [];
    let cur = '';
    let quoted = false;
    for (let i = 0; i < rawLine.length; i++) {
      const ch = rawLine[i];
      if (quoted) {
        if (ch === '"' && rawLine[i + 1] === '"') { cur += '"'; i++; } else if (ch === '"') quoted = false; else cur += ch;
      } else if (ch === '"') quoted = true;
      else if (ch === ',') { cells.push(cur.trim()); cur = ''; } else cur += ch;
    }
    cells.push(cur.trim());
    rows.push(cells);
  }
  // A header row is optional: recognised by a first cell that is not a path.
  if (rows.length && !rows[0][0].startsWith('/') && /from/i.test(rows[0][0])) rows.shift();
  return rows.map(([from_path = '', to_path = '', status_code = '', note = '']) => ({
    from_path, to_path, ...(status_code ? { status_code } : {}), ...(note ? { note } : {}),
  }));
}

function ProblemList({ problems }) {
  if (!problems?.length) return null;
  return (
    <div className="field-error full" style={{ display: 'block' }}>
      <b>Involved:</b>
      <ul style={{ margin: '6px 0 0 18px' }}>
        {problems.map((p, i) => (
          <li key={i}>{PROBLEM_LABEL[p.kind] || p.kind}: <code>{p.from_path}</code>{p.to_path ? <> → <code>{p.to_path}</code></> : null}</li>
        ))}
      </ul>
    </div>
  );
}

function RedirectModal({ brand, row, onClose, onSaved }) {
  const isEdit = Boolean(row);
  const [form, setForm] = useState({
    from_path: row?.from_path || '',
    to_path: row?.to_path || '',
    status_code: String(row?.status_code || 301),
    note: row?.note || '',
    is_active: row ? Boolean(row.is_active) : true,
  });
  const [errors, setErrors] = useState({});
  const [problems, setProblems] = useState(null);
  const [message, setMessage] = useState('');

  const mut = useMutation({
    mutationFn: (body) => (isEdit ? updateRedirect(row.id, body) : createRedirect({ brand, ...body })),
    onSuccess: () => { toast.ok('Saved', 'Redirect saved.'); onSaved(); },
    onError: (err) => {
      const f = errFields(err);
      setProblems(f.problems || null);
      setErrors(f.problems ? {} : f);
      setMessage(errMessage(err, 'Could not save'));
    },
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));
  const gone = form.status_code === '410';

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 560 }}>
        <div className="lk-modal-head">
          <h3>{isEdit ? 'Edit redirect' : 'Add redirect'}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>
        <form
          className="det-body"
          onSubmit={(e) => {
            e.preventDefault();
            setMessage(''); setProblems(null);
            mut.mutate({ ...form, status_code: Number(form.status_code), note: form.note || null });
          }}
        >
          <div className="field full">
            <label>From path</label>
            <input value={form.from_path} onChange={set('from_path')} placeholder="/old-page" maxLength={500} autoFocus />
            <div className="field-hint muted">A path on this storefront, starting with “/”. Query strings are ignored.</div>
            {errors.from_path ? <div className="field-error">{errors.from_path}</div> : null}
          </div>
          <div className="field full">
            <label>To {gone ? '(not used for 410)' : ''}</label>
            <input value={form.to_path} onChange={set('to_path')} placeholder="/new-page or https://…" maxLength={500} disabled={gone && !form.to_path} />
            {errors.to_path ? <div className="field-error">{errors.to_path}</div> : null}
          </div>
          <div className="field full">
            <label>Status</label>
            <Select value={form.status_code} onChange={set('status_code')} options={STATUS_OPTIONS} />
            {errors.status_code ? <div className="field-error">{errors.status_code}</div> : null}
          </div>
          <div className="field full">
            <label>Note</label>
            <input value={form.note} onChange={set('note')} maxLength={500} placeholder="Why this redirect exists" />
          </div>
          <div className="field full">
            <label className="check">
              <input type="checkbox" checked={form.is_active} onChange={set('is_active')} />
              <span>Active</span>
            </label>
          </div>
          {message ? <div className="field-error full">{message}</div> : null}
          <ProblemList problems={problems} />
          <div className="form-actions">
            <button type="button" className="btn ghost" onClick={onClose} disabled={mut.isPending}>Cancel</button>
            <button type="submit" className="btn primary" disabled={mut.isPending}>{mut.isPending ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImportModal({ brand, onClose, onSaved }) {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const rows = parseCsv(text);

  const mut = useMutation({
    mutationFn: () => importRedirects({ brand, rows }),
    onSuccess: (res) => { toast.ok('Imported', `${res.data?.imported ?? rows.length} redirect(s) added.`); onSaved(); },
    onError: (err) => {
      const f = errFields(err);
      setResult({ message: errMessage(err, 'Import failed'), rows: Array.isArray(f.rows) ? f.rows : null, problems: f.problems || null });
    },
  });

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setText(await file.text());
    setResult(null);
    e.target.value = '';
  }

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 680 }}>
        <div className="lk-modal-head">
          <h3>Import redirects (CSV)</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>
        <div className="det-body">
          <p className="muted">
            One redirect per line: <code>from_path,to_path[,status_code][,note]</code>. A header row is optional.
            Status defaults to 301. All or nothing — if any line is wrong, nothing is saved and every failing line is listed.
            At most 2,000 lines per file.
          </p>
          <div className="field full">
            <label>File</label>
            <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} />
          </div>
          <div className="field full">
            <label>…or paste</label>
            <textarea className="seo-code" rows={8} value={text} onChange={(e) => { setText(e.target.value); setResult(null); }}
              spellCheck={false} placeholder={'/old-offers,/offers,301\n/summer-sale,/offers'} />
            <div className="field-hint muted">{rows.length} row(s) read.</div>
          </div>
          {result ? (
            <div className="field-error full" style={{ display: 'block' }}>
              <b>{result.message}</b>
              {result.rows ? (
                <ul style={{ margin: '6px 0 0 18px', maxHeight: 200, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                  {result.rows.map((r) => (
                    <li key={r.row}>
                      Line {r.row}{r.from_path ? <> (<code>{String(r.from_path)}</code>)</> : null}: {Object.values(r.errors || {}).join(' · ')}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          <ProblemList problems={result?.problems} />
          <div className="form-actions">
            <button type="button" className="btn ghost" onClick={onClose} disabled={mut.isPending}>Cancel</button>
            <button type="button" className="btn primary" disabled={!rows.length || rows.length > 2000 || mut.isPending} onClick={() => mut.mutate()}>
              {mut.isPending ? 'Importing…' : `Import ${rows.length || ''} row(s)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SeoRedirects() {
  const qc = useQueryClient();
  const [brand, setBrand] = useSeoBrand();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [active, setActive] = useState('');
  const [modal, setModal] = useState(null); // { kind: 'edit', row } | { kind: 'import' }

  const params = { brand, page, pageSize: PAGE_SIZE };
  if (applied) params.q = applied;
  if (active) params.isActive = active;

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.seo.redirects(params),
    queryFn: () => listRedirects(params),
    enabled: Boolean(brand),
    placeholderData: keepPreviousData,
  });
  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.seo.root });
  const removeMut = useMutation({
    mutationFn: (id) => deleteRedirect(id),
    onSuccess: () => { toast.ok('Deleted', 'Redirect removed.'); invalidate(); },
    onError: (err) => toast.err('Failed', errMessage(err, 'Could not delete')),
  });

  async function onDelete(r) {
    if (!(await confirm(`Delete the redirect from “${r.from_path}”? Visitors to that URL will get a 404.`))) return;
    removeMut.mutate(r.id);
  }
  const closeAndRefresh = () => { setModal(null); invalidate(); };

  return (
    <>
      <PageHead
        title="Redirects"
        subtitle="SEO Management · old URLs the storefront sends somewhere else"
        actions={brand ? (
          <>
            <button className="btn ghost" onClick={() => setModal({ kind: 'import' })}><Icon name="download" /> Import CSV</button>
            <button className="btn primary" onClick={() => setModal({ kind: 'edit', row: null })}><Icon name="plus" /> Add Redirect</button>
          </>
        ) : null}
      />
      <BrandBar brand={brand} onChange={(v) => { setPage(1); setBrand(v); }} />
      {!brand ? <PickBrand /> : (
        <article className="panel glass">
          <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search.trim()); }}>
            <div className="field">
              <label>From / To path</label>
              <input placeholder="Search redirects…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="field">
              <label>Status</label>
              <Select value={active} onChange={(v) => { setPage(1); setActive(v); }} options={ACTIVE_FILTER} />
            </div>
            <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
          </form>

          <div className="table-wrap">
            {isLoading ? <TableSkeleton /> : isError ? (
              <div className="empty"><p className="field-error">Could not load redirects.</p></div>
            ) : (
              <table className="table">
                <thead>
                  <tr><th>From</th><th>To</th><th>Code</th><th>Hits</th><th>Last hit</th><th>Status</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={7} className="empty">No redirects{applied || active ? ' match these filters' : ' yet'}.</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.id} style={{ verticalAlign: 'middle' }}>
                      <td className="mono" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', maxWidth: 280 }}>{r.from_path}</td>
                      <td className="mono" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', maxWidth: 320 }}>
                        {Number(r.status_code) === 410 ? <span className="muted">— (gone)</span> : r.to_path}
                        {r.note ? <div className="muted" style={{ fontSize: '.72rem' }}>{r.note}</div> : null}
                      </td>
                      <td className="mono">{r.status_code}</td>
                      <td className="mono">{r.hits}</td>
                      <td>{r.last_hit_at ? formatDateTime(r.last_hit_at) : <span className="muted">Never</span>}</td>
                      <td>
                        <Chip tone={r.is_active ? 'ok' : 'rej'}>{r.is_active ? 'Active' : 'Inactive'}</Chip>
                        {r.problem ? <><br /><Chip tone="rej">{PROBLEM_LABEL[r.problem] || r.problem}</Chip></> : null}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button className="ib edit" aria-label="Edit" onClick={() => setModal({ kind: 'edit', row: r })}><Icon name="edit" /></button>
                          <button className="ib del" aria-label="Delete" onClick={() => onDelete(r)}><Icon name="trash" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <Pager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} />
        </article>
      )}

      {modal?.kind === 'edit' ? (
        <RedirectModal brand={brand} row={modal.row} onClose={() => setModal(null)} onSaved={closeAndRefresh} />
      ) : null}
      {modal?.kind === 'import' ? (
        <ImportModal brand={brand} onClose={() => setModal(null)} onSaved={closeAndRefresh} />
      ) : null}
    </>
  );
}
